<template>
  <div class="surgery-form-container">
    <!-- Compact Go Back Button -->
    <div class="go-back-container mb-1">
      <b-button variant="outline-secondary" size="sm" @click="$router.go(-1)">
        <feather-icon icon="ArrowLeftIcon" size="16" />
        {{ $t("common.go-back") }}
      </b-button>
    </div>

    <!-- Single Combined Card -->
    <b-card class="main-card">
      <b-card-text class="card-content">
        <!-- Surgery Information Section -->
        <div class="section section-primary">
          <div class="section-header">
            <feather-icon icon="ActivityIcon" size="18" class="section-icon" />
            <span class="section-title-text">{{
              isEditMode
                ? $t("surgery.Edit-Surgery")
                : $t("surgery.Create-Surgery")
            }}</span>
          </div>
          <b-row class="g-3">
            <b-col md="4" cols="12">
              <b-form-group
                :label="$t('reception.type')"
                label-for="surgery-type"
                class="mb-2"
              >
                <AutoComplete
                  id="surgery-type"
                  :placeholder="$t('reception.available-surgeries-for-doctors')"
                  v-model="selectedAvailableSurgeries"
                  :serializer="(val) => val"
                  param-query="filter[id]=-id"
                  other-query=""
                  endpoint="surgery"
                  text-title="title"
                  @input="onSurgerySelected"
                  class="compact-select"
                >
                  <template #list-footer>
                    <b-button
                      variant="success"
                      size="sm"
                      block
                      class="m-1"
                      @click="showCreateSurgeryModal"
                    >
                      {{ $t("reception.create-surgery") }}
                    </b-button>
                  </template>
                </AutoComplete>
              </b-form-group>
            </b-col>
            <b-col md="4" cols="12">
              <b-form-group
                :label="$t('common.date')"
                label-for="surgery-date"
                class="mb-2"
              >
                <datetime
                  id="surgery-date"
                  input-class="form-control compact-input"
                  type="datetime"
                  v-model="date"
                />
              </b-form-group>
            </b-col>
            <b-col md="4" cols="12">
              <b-form-group
                :label="$t('common.price')"
                label-for="surgery-cost"
                class="mb-2"
              >
                <b-form-input
                  id="surgery-cost"
                  type="number"
                  v-model="EditForm.price"
                  :placeholder="$t('common.price')"
                  min="0"
                  step="0.01"
                  class="compact-input"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-checkbox
                v-model="EditForm.money_collected"
                name="checkbox-money-collected"
              >
                {{ $t("surgery.dose-receved-money") }}
              </b-form-checkbox>
            </b-col>
          </b-row>
        </div>

        <!-- Create Surgery Modal -->
        <b-modal
          id="modal-create-surgery"
          :title="$t('reception.create-surgery')"
          ok-only
          centered
          size="lg"
          :no-close-on-backdrop="true"
        >
          <div>
            <b-form-group :label="$t('common.title')">
              <b-form-input v-model="surgeryForm.title" />
            </b-form-group>
            <b-form-group :label="$t('common.description')">
              <b-form-textarea v-model="surgeryForm.description" />
            </b-form-group>
            <b-form-group :label="$t('reception.type')">
              <v-select
                v-model="surgeryForm.type"
                :options="surgeryTypesList"
                label="text"
                :reduce="(option) => option.value"
              />
            </b-form-group>
          </div>
          <template #modal-footer="{}">
            <b-button
              variant="primary"
              @click="createSurgery"
              class="m-0"
              :disabled="loading"
              ref="button"
              block
            >
              <span v-if="!loading">{{ $t("common.add") }}</span>
              <b-spinner small v-else></b-spinner>
            </b-button>
          </template>
        </b-modal>

        <!-- Main Doctors Section -->
        <div class="section section-doctor">
          <div class="section-header">
            <feather-icon icon="UserIcon" size="18" class="section-icon" />
            <span class="section-title-text">{{
              $t("surgery.main-doctors")
            }}</span>
          </div>
          <b-form-group :label="$t('surgery.main-doctors')" class="mb-1">
            <v-select
              v-model="selectedDoctors"
              :options="doctors"
              multiple
              label="name"
              :reduce="(doctor) => doctor"
              :close-on-select="false"
              @input="onDoctorsChange"
              class="compact-select"
            >
              <template #option="{ name, type }">
                <b style="margin: 0">
                  {{ name }}
                  <span class="text-primary">
                    ({{
                      type == "surgeon"
                        ? $t("hospital.surgeon")
                        : $t("hospital.anesthetist")
                    }})
                  </span>
                </b>
              </template>
              <template #selected-option="{ name, type }">
                <b style="margin: 0">
                  {{ name }}
                  <span class="text-success">
                    ({{
                      type == "surgeon"
                        ? $t("hospital.surgeon")
                        : $t("hospital.anesthetist")
                    }})
                  </span>
                </b>
              </template>
            </v-select>
          </b-form-group>

          <!-- Doctor Cards -->
          <div v-if="doctorCards.length > 0" class="cards-grid mt-2">
            <div
              v-for="(doctorCard, index) in doctorCards"
              :key="doctorCard.id"
              class="doctor-card-wrapper"
            >
              <b-card class="doctor-card">
                <div class="card-header">
                  <div class="doctor-info">
                    <feather-icon
                      icon="UserIcon"
                      size="16"
                      class="doctor-icon"
                    />
                    <div>
                      <h6 class="mb-0">{{ doctorCard.name }}</h6>
                      <small class="text-muted">
                        {{
                          doctorCard.type == "surgeon"
                            ? $t("hospital.surgeon")
                            : $t("hospital.anesthetist")
                        }}
                      </small>
                    </div>
                  </div>
                  <b-button
                    size="sm"
                    variant="outline-danger"
                    @click="removeDoctorCard(index)"
                    class="remove-btn"
                  >
                    <feather-icon icon="XIcon" size="14" />
                  </b-button>
                </div>
              </b-card>
            </div>
          </div>
        </div>

        <!-- Doctor Assistants Section -->
        <div class="section section-assistant">
          <div class="section-header">
            <feather-icon icon="UsersIcon" size="18" class="section-icon" />
            <span class="section-title-text">{{
              $t("surgery.Doctor Assist Info")
            }}</span>
          </div>
          <b-form-group :label="$t('surgery.doctor-assistants')" class="mb-1">
            <v-select
              v-model="selectedDoctorAssistants"
              :options="doctorAssistants"
              multiple
              :close-on-select="false"
              @input="onAssistantsChange"
              class="compact-select"
            >
              <template #option="{ name, type }">
                <b style="margin: 0">
                  {{ name }}
                  <span class="text-primary">
                    ({{
                      type == "surgeon_assistant"
                        ? $t("hospital.surgeon-assistant")
                        : $t("hospital.anesthetist-assistant")
                    }})
                  </span>
                </b>
              </template>
              <template #selected-option="{ name, type }">
                <b style="margin: 0">
                  {{ name }}
                  <span class="text-success">
                    ({{
                      type == "surgeon_assistant"
                        ? $t("hospital.surgeon-assistant")
                        : $t("hospital.anesthetist-assistant")
                    }})
                  </span>
                </b>
              </template>
            </v-select>
          </b-form-group>

          <!-- Assistant Cards -->
          <div v-if="assistantCards.length > 0" class="cards-grid mt-3">
            <div
              v-for="(assistantCard, index) in assistantCards"
              :key="assistantCard.id"
              class="doctor-card-wrapper"
            >
              <b-card class="doctor-card">
                <div class="card-header">
                  <div class="doctor-info">
                    <feather-icon
                      icon="UsersIcon"
                      size="16"
                      class="doctor-icon"
                    />
                    <div>
                      <h6 class="mb-0">{{ assistantCard.name }}</h6>
                      <small class="text-muted">
                        {{
                          assistantCard.type == "surgeon_assistant"
                            ? $t("hospital.surgeon-assistant")
                            : $t("hospital.anesthetist-assistant")
                        }}
                      </small>
                    </div>
                  </div>
                  <b-button
                    size="sm"
                    variant="outline-danger"
                    @click="removeAssistantCard(index)"
                    class="remove-btn"
                  >
                    <feather-icon icon="XIcon" size="14" />
                  </b-button>
                </div>
              </b-card>
            </div>
          </div>
        </div>

        <!-- Patient Medical Info -->
        <div class="section section-medical">
          <div class="section-header">
            <feather-icon icon="FileTextIcon" size="18" class="section-icon" />
            <span class="section-title-text">{{
              $t("emergency.Patient Medical Info")
            }}</span>
          </div>
          <b-row class="g-2">
            <b-col md="12">
              <b-form-group
                :label="$t('reception.patient-case')"
                label-for="patient-case"
              >
                <b-form-textarea
                  id="patient-case"
                  v-model="patientCardData.patient_case"
                  :placeholder="$t('reception.patient-case')"
                  rows="2"
                  class="compact-input"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('reception.diagnosis')"
                label-for="diagnosis"
              >
                <b-form-textarea
                  id="diagnosis"
                  v-model="patientCardData.diagnosis"
                  :placeholder="$t('reception.diagnosis')"
                  rows="2"
                  class="compact-input"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('reception.investigation')"
                label-for="investigation"
              >
                <b-form-textarea
                  id="investigation"
                  v-model="patientCardData.investigation"
                  :placeholder="$t('reception.investigation')"
                  rows="2"
                  class="compact-input"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('reception.examination')"
                label-for="examination"
              >
                <b-form-textarea
                  id="examination"
                  v-model="patientCardData.examination"
                  :placeholder="$t('reception.examination')"
                  rows="2"
                  class="compact-input"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('reception.treatment')"
                label-for="treatment"
              >
                <b-form-textarea
                  id="treatment"
                  v-model="patientCardData.treatment"
                  :placeholder="$t('reception.treatment')"
                  rows="2"
                  class="compact-input"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <!-- Services Section -->
        <div class="section section-services">
          <div class="section-header">
            <feather-icon icon="LayersIcon" size="18" class="section-icon" />
            <span class="section-title-text">{{
              $t("hospital.services")
            }}</span>
          </div>
          <ServicePriceAutoComplete
            v-model="selectedServices"
            :label="$t('hospital.services')"
            :placeholder="$t('hospital.services')"
            :serializer="(val) => val"
            param-query="search"
            text-title="title"
            :multiple="true"
            :md="12"
            @val="handleServicesChange"
            @total-price="handleTotalPrice"
          />
        </div>

        <!-- Staff Section -->
        <!-- <div class="section section-staff">
          <div class="section-header">
            <feather-icon icon="UserCheckIcon" size="18" class="section-icon" />
            <span class="section-title-text">{{
              $t("surgery.Surgery Staff")
            }}</span>
          </div>
          <div class="chips-container mb-1" v-if="EditForm.staff.length > 0">
            <b-badge
              v-for="(staff, staffIndex) in EditForm.staff"
              :key="staffIndex"
              variant="primary"
              class="chip-badge"
            >
              {{ staff }}
              <span @click="removeStaff(staffIndex)" class="chip-remove">
                <feather-icon icon="XIcon" size="12" />
              </span>
            </b-badge>
          </div>
          <div class="add-input-row">
            <b-form-input
              v-model="newStaff"
              size="sm"
              :placeholder="$t('surgery.add-staff')"
              class="compact-input"
              @keyup.enter="addStaff"
            />
            <b-button
              size="sm"
              variant="primary"
              @click="addStaff"
              class="add-btn"
            >
              <feather-icon icon="PlusIcon" size="14" />
            </b-button>
          </div>
        </div> -->

        <!-- Consumables Section -->
        <div class="section section-consumables">
          <div class="section-header">
            <feather-icon icon="PackageIcon" size="18" class="section-icon" />
            <span class="section-title-text">{{
              $t("surgery.consumables")
            }}</span>
          </div>
          <div
            class="chips-container mb-1"
            v-if="EditForm.consumables.length > 0"
          >
            <b-badge
              v-for="(consumable, consIndex) in EditForm.consumables"
              :key="consIndex"
              variant="info"
              class="chip-badge"
            >
              {{ consumable.name }} ({{ consumable.quantity }})
              <span @click="removeConsumable(consIndex)" class="chip-remove">
                <feather-icon icon="XIcon" size="12" />
              </span>
            </b-badge>
          </div>
          <b-row class="g-2 align-items-center">
            <b-col md="4" cols="12">
              <b-form-group class="mb-0">
                <CategoryModal @category="assignCategory" />
              </b-form-group>
            </b-col>
            <b-col v-if="sciFilter" md="4" cols="12">
              <b-form-group class="mb-0">
                <AutoComplete
                  v-model="selectedConsumable"
                  :label="''"
                  :placeholder="$t('surgery.select-consumable')"
                  param-query="search"
                  :serializer="(val) => val"
                  endpoint="item"
                  text-title="name"
                  :otherQuery="`filter[category_id]=${sciFilter}`"
                />
              </b-form-group>
            </b-col>
            <b-col md="2" cols="12">
              <b-form-group class="mb-0">
                <b-form-input
                  v-model="consumableQuantity"
                  type="number"
                  size="sm"
                  :placeholder="$t('pharmacy.Quantity')"
                  min="1"
                  class="compact-input"
                />
              </b-form-group>
            </b-col>
            <b-col md="2" cols="12">
              <b-button
                size="sm"
                variant="primary"
                @click="addConsumable"
                :disabled="!selectedConsumable || !consumableQuantity"
                block
                class="add-btn"
              >
                <feather-icon icon="PlusIcon" size="14" />
              </b-button>
            </b-col>
          </b-row>
        </div>

        <!-- Surgery Procedure -->
        <div class="section section-procedure">
          <div class="section-header">
            <feather-icon icon="EditIcon" size="18" class="section-icon" />
            <span class="section-title-text">{{
              $t("surgery.surgery-procedure")
            }}</span>
          </div>
          <b-form-group
            :label="$t('surgery.procedure')"
            label-for="procedure"
            class="mb-0"
          >
            <b-form-textarea
              id="procedure"
              v-model="EditForm.procedure"
              :placeholder="$t('surgery.procedure')"
              rows="3"
              class="compact-input"
            />
          </b-form-group>
        </div>
      </b-card-text>
    </b-card>

    <!-- Submit Button -->
    <div class="submit-container">
      <b-button
        variant="primary"
        @click="submitItem"
        :disabled="loading || !isFormValid"
        size="lg"
        class="submit-btn"
      >
        <feather-icon v-if="!loading" icon="CheckIcon" size="18" />
        <b-spinner small v-else></b-spinner>
        <span v-if="!loading" class="ml-2">
          {{ isEditMode ? $t("common.update") : $t("common.submit") }}
        </span>
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCardText,
  BFormGroup,
  BButton,
  BCol,
  BRow,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BSpinner,
  BBadge,
} from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
const AutoComplete = () => import("@/components/common/AutoComplete.vue");
const ServicePriceAutoComplete = () =>
  import("@/components/common/ServicePriceAutoComplete.vue");
import "vue-phone-number-input/dist/vue-phone-number-input.css";
import vSelect from "vue-select";
import { Datetime } from "vue-datetime";
import "vue-datetime/dist/vue-datetime.css";
const CategoryModal = () => import("@/components/warehouse/CategoryModal.vue");

export default {
  components: {
    BCard,
    BCardText,
    BFormGroup,
    BButton,
    BCol,
    BRow,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BSpinner,
    BBadge,
    AutoComplete,
    CategoryModal,
    ServicePriceAutoComplete,
    Datetime,
    vSelect,
  },

  props: ["patientCardId"],

  data() {
    return {
      loading: false,
      isEditMode: false,
      editSurgeryId: null,
      date: new Date(Date.now()).toISOString(),
      doctors: [],
      doctorAssistants: [],
      selectedDoctors: [],
      selectedDoctorAssistants: [],
      doctorCards: [],
      assistantCards: [],
      selectedServices: [],
      totalAmount: 0,
      availableSurgeriesOptions: [],
      selectedAvailableSurgeries: null,
      doctorsId: "",
      doctorsName: "",
      patientCardData: {},
      newStaff: "",
      selectedConsumable: null,
      consumableQuantity: 1,
      sciFilter: null,
      EditForm: {
        id: null,
        patient_card_id: "",
        surgery_id: "",
        surgery_date_time: "",
        price: "",
        procedure: "",
        invoices: [],
        staff: [],
        consumables: [],
        money_collected: true,
        _method: "POST",
      },
      errorForm: {
        surgery_id: "",
        surgery_date_time: "",
        price: "",
      },
      surgeryForm: {
        title: "",
        description: "",
        type: 0,
        price: 0,
      },
      surgeryTypesList: [
        { text: "كبرى ", value: "1" },
        { text: "صغرى ", value: "2" },
        { text: "متوسطة", value: "3" },
        { text: "فوق الكبرى", value: "4" },
        { text: "خاصة", value: "5" },
      ],
      paramsForSurgery: {
        form: {},
        path: "/surgery",
      },
      paramsForDoctorSurgery: {
        form: {},
        path: "/doctorSurgery",
      },
      params: {
        form: {},
        path: "",
      },
    };
  },

  computed: {
    isFormValid() {
      return (
        this.selectedDoctors.length > 0 &&
        this.selectedAvailableSurgeries &&
        this.EditForm.price !== "" &&
        parseFloat(this.EditForm.price) >= 0
      );
    },
  },

  mounted() {
    // Check if we're in edit mode
    if (this.$route.query.editSurgeryId) {
      this.isEditMode = true;
      this.editSurgeryId = this.$route.query.editSurgeryId;
      this.EditForm._method = "PUT";
    }

    this.getPatientCard();
    this.getDoctors();
    this.getDoctorAssistants();

    if (this.isEditMode) {
      this.getSurgeryDataForEdit();
    } else if (this.$route.query.isSub) {
      this.getPatientSurgeryInfo();
    }
  },

  watch: {
    selectedDoctors() {
      this.formatForQuery(this.selectedDoctors);
    },
  },

  methods: {
    async getDoctors() {
      await this.getList("doctor?take=100").then((response) => {
        this.doctors = response.data.data.map((doctor) => ({
          id: doctor.id,
          name: doctor.name,
          type: doctor.type,
        }));
      });
    },

    async getDoctorAssistants() {
      try {
        const response = await this.getList("/doctorAssistants");
        this.doctorAssistants = response.data.data;
      } catch (error) {
        console.error("Error fetching doctor assistants:", error);
      }
    },

    handleServicesChange(services) {
      this.selectedServices = services;
      // Convert selected services to invoices format with amount and service_id
      if (Array.isArray(services)) {
        this.EditForm.invoices = services.map((service) => ({
          amount: parseFloat(service.price || 0),
          service_id: service.id,
        }));
      } else if (services) {
        this.EditForm.invoices = [
          {
            amount: parseFloat(services.price || 0),
            service_id: services.id,
          },
        ];
      } else {
        this.EditForm.invoices = [];
      }
    },

    handleTotalPrice(total) {
      this.totalAmount = total;
    },

    async formatForQuery(val) {
      this.doctorsName = this.getDoctorsName(val);
      this.availableSurgeriesOptions = [];
      if (val.length === 0) {
        return;
      }
      let temp = ``;
      val.forEach((item) => {
        temp += `doctors[]=${item.id}&`;
      });
      this.doctorsId = temp;
      await this.getList(
        `surgery/availableSurgeriesForDoctors?${this.doctorsId}`,
      );
      this.availableSurgeries = this.all_data;
      this.availableSurgeries.forEach((item) => {
        this.availableSurgeriesOptions.push({
          id: item.id,
          title: item.title,
        });
      });
    },

    getDoctorsName(doctors) {
      let temp = "";
      doctors.forEach((item) => {
        temp += item.name + ", ";
      });
      return temp;
    },

    onDoctorsChange(doctors) {
      // Update doctor cards based on selected doctors
      const currentIds = this.doctorCards.map((card) => card.id);
      const selectedIds = doctors.map((doc) => doc.id);

      // Remove cards for unselected doctors
      this.doctorCards = this.doctorCards.filter((card) =>
        selectedIds.includes(card.id),
      );

      // Add cards for newly selected doctors
      doctors.forEach((doctor) => {
        if (!currentIds.includes(doctor.id)) {
          this.doctorCards.push({
            id: doctor.id,
            name: doctor.name,
            type: doctor.type,
          });
        }
      });
    },

    onAssistantsChange(assistants) {
      // Update assistant cards based on selected assistants
      const currentIds = this.assistantCards.map((card) => card.id);
      const selectedIds = assistants.map((ass) => ass.id);

      // Remove cards for unselected assistants
      this.assistantCards = this.assistantCards.filter((card) =>
        selectedIds.includes(card.id),
      );

      // Add cards for newly selected assistants
      assistants.forEach((assistant) => {
        if (!currentIds.includes(assistant.id)) {
          this.assistantCards.push({
            id: assistant.id,
            name: assistant.name,
            type: assistant.type,
          });
        }
      });
    },

    removeDoctorCard(index) {
      const card = this.doctorCards[index];
      this.selectedDoctors = this.selectedDoctors.filter(
        (doc) => doc.id !== card.id,
      );
      this.doctorCards.splice(index, 1);
    },

    removeAssistantCard(index) {
      const card = this.assistantCards[index];
      this.selectedDoctorAssistants = this.selectedDoctorAssistants.filter(
        (ass) => ass.id !== card.id,
      );
      this.assistantCards.splice(index, 1);
    },

    // Staff Methods
    addStaff() {
      if (this.newStaff.trim()) {
        this.EditForm.staff.push(this.newStaff.trim());
        this.newStaff = "";
      }
    },

    removeStaff(staffIndex) {
      this.EditForm.staff.splice(staffIndex, 1);
    },

    // Consumables Methods
    assignCategory(category) {
      this.selectedConsumable = null;
      this.sciFilter = category.id;
    },

    addConsumable() {
      if (this.selectedConsumable && this.consumableQuantity) {
        this.EditForm.consumables.push({
          item_id: this.selectedConsumable.id,
          name: this.selectedConsumable.name,
          quantity: parseInt(this.consumableQuantity),
        });
        this.selectedConsumable = null;
        this.consumableQuantity = 1;
        this.sciFilter = null;
      }
    },

    removeConsumable(consIndex) {
      this.EditForm.consumables.splice(consIndex, 1);
    },

    onSurgerySelected(surgery) {
      if (surgery) {
        this.EditForm.surgery_id = surgery.id;
      }
    },

    showCreateSurgeryModal() {
      this.$bvModal.show("modal-create-surgery");
    },

    async createSurgery() {
      this.loading = true;
      try {
        this.paramsForSurgery.form = this.surgeryForm;
        this.paramsForSurgery.path = "/surgery";
        this.$store
          .dispatch("create", this.paramsForSurgery)
          .then(async (surgeryResponse) => {
            // Link doctors to the created surgery
            const doctorSurgeryPromises = this.selectedDoctors.map((doctor) => {
              this.paramsForDoctorSurgery.form = {
                doctor_id: doctor.id,
                surgery_id: surgeryResponse.data.data.id,
                price: 0,
              };
              this.paramsForDoctorSurgery.path = "/doctorSurgery";
              return this.$store.dispatch(
                "create",
                this.paramsForDoctorSurgery,
              );
            });

            await Promise.all(doctorSurgeryPromises);
            this.loading = false;
            this.$bvModal.hide("modal-create-surgery");
            await this.formatForQuery(this.selectedDoctors);
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "Success",
                variant: "success",
                text: this.$t(
                  "reception.surgery-created-and-linked-to-doctors-successfully",
                ),
              },
            });
          })
          .catch((error) => {
            this.loading = false;
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "Error",
                variant: "danger",
                text:
                  error.response?.data?.message ||
                  this.$t(
                    "reception.surgery-created-and-linked-to-doctors-error",
                  ),
              },
            });
          });
      } catch (error) {
        this.loading = false;
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Error",
            variant: "danger",
            text:
              error.response?.data?.message ||
              this.$t("reception.surgery-created-and-linked-to-doctors-error"),
          },
        });
      }
    },

    async getPatientCard() {
      this.loading = true;
      await this.getPatient(
        `active-card/patient/${this.$route.query.patientId}?include=patient,doctors,surgeries`,
      );
      this.loading = false;
      for (let item in this.all_data) {
        if (this.all_data[item] == null) this.all_data[item] = "";
      }
      this.patientCardData = this.all_data.data;
      this.setDoctors();
    },

    async getPatientSurgeryInfo() {
      await this.getPatient(
        `patient-surgery?filter[patient_card_id]=${this.$route.params.id}&include=surgery`,
      );
      this.surgeryList = this.all_data.data.map((item) => ({
        title: item.surgery.title,
        value: item.id,
      }));
    },

    async getSurgeryDataForEdit() {
      if (!this.editSurgeryId) return;

      this.loading = true;
      try {
        await this.getList(
          `patient-surgery/${this.editSurgeryId}?include=doctorAssist,surgeryStaff,patientCard,doctors,surgery`,
        );

        const surgeryData = this.all_data;

        // Populate form with existing data
        this.EditForm.id = surgeryData.id;
        this.EditForm.surgery_id = surgeryData.surgery_id;
        this.EditForm.price = surgeryData.price || "";
        this.EditForm.procedure = surgeryData.procedure || "";
        this.EditForm.money_collected = surgeryData.money_collected === 1;
        this.EditForm.patient_card_id = surgeryData.patient_card_id;

        // Set date
        if (surgeryData.surgery_date_time) {
          this.date = new Date(surgeryData.surgery_date_time).toISOString();
        }

        // Set selected surgery
        if (surgeryData.surgery) {
          this.selectedAvailableSurgeries = {
            id: surgeryData.surgery.id,
            title: surgeryData.surgery.title,
          };
        }

        // Populate doctors
        if (surgeryData.doctors_prices && surgeryData.doctors_prices.items) {
          this.selectedDoctors = surgeryData.doctors_prices.items.map(
            (item) => ({
              id: item.doctor_id,
              name: item.doctor.name,
              type: item.type,
            }),
          );
          this.onDoctorsChange(this.selectedDoctors);
          await this.formatForQuery(this.selectedDoctors);
        }

        // Populate assistants
        if (surgeryData.doctor_assist && surgeryData.doctor_assist.length > 0) {
          this.selectedDoctorAssistants = surgeryData.doctor_assist.map(
            (assist) => ({
              id: assist.id,
              name: assist.name,
              type: assist.type,
            }),
          );
          this.onAssistantsChange(this.selectedDoctorAssistants);
        }

        // Populate staff
        if (surgeryData.surgery_staff && surgeryData.surgery_staff.length > 0) {
          this.EditForm.staff = surgeryData.surgery_staff.map(
            (staff) => staff.name || staff.employee?.name || "",
          );
        }

        // Populate consumables
        if (surgeryData.consumables && surgeryData.consumables.length > 0) {
          this.EditForm.consumables = surgeryData.consumables.map((cons) => ({
            item_id: cons.item_id,
            name: cons.item?.name || "",
            quantity: cons.quantity,
          }));
        }

        // Note: Invoices/services are not available in the patient-surgery endpoint
        // They need to be fetched separately if needed

        // Populate patient medical info
        if (surgeryData.patient_card) {
          this.patientCardData.patient_case = surgeryData.patient_case || "";
          this.patientCardData.examination = surgeryData.examination || "";
          this.patientCardData.treatment = surgeryData.treatment || "";
          this.patientCardData.diagnosis = surgeryData.diagnosis || "";
          this.patientCardData.investigation = surgeryData.investigation || "";
        }
      } catch (error) {
        console.error("Error fetching surgery data for edit:", error);
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Error",
            variant: "danger",
            text: "Failed to load surgery data for editing",
          },
        });
      } finally {
        this.loading = false;
      }
    },

    setDoctors() {
      if (
        this.patientCardData.doctors &&
        this.patientCardData.doctors.length > 0
      ) {
        this.selectedDoctors = [
          ...this.patientCardData.doctors.map((doctor) => ({
            id: doctor.id,
            name: doctor.name,
            type: "surgeon",
          })),
        ];
        this.onDoctorsChange(this.selectedDoctors);
        this.formatForQuery(this.patientCardData.doctors);
      } else {
        this.selectedDoctors = [];
      }
    },

    formatNewDate(surgeryDate) {
      const date = new Date(surgeryDate);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      // Backend expects format Y-m-d H:i (no seconds)
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },

    submitItem() {
      // Validation
      let count = 0;
      this.loading = true;
      this.errorForm.surgery_id = "";
      this.errorForm.price = "";

      if (!this.EditForm.surgery_id) {
        count++;
        this.errorForm.surgery_id = "The surgery field is required.";
      }

      if (!this.EditForm.price || parseFloat(this.EditForm.price) < 0) {
        count++;
        this.errorForm.price = "Surgery cost is required and must be >= 0.";
      }

      if (this.selectedDoctors.length === 0) {
        count++;
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Validation Error",
            variant: "danger",
            text: "At least one doctor must be selected.",
          },
        });
      }

      if (count > 0) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Validation Error",
            variant: "danger",
            text: this.errorForm.surgery_id || this.errorForm.price,
          },
        });
        this.loading = false;
        return false;
      }

      // Prepare form data
      this.EditForm.patient_card_id = this.$route.params.id;
      this.EditForm.parent_id = this.$route.query.surgeryId;

      // Only set surgery_date_time if creating new surgery or if date is in the future (for updates)
      if (!this.isEditMode) {
        // For new surgeries, always set the date
        this.EditForm.surgery_date_time = this.formatNewDate(this.date);
      } else {
        // For updates, only include surgery_date_time if it's a future date
        const selectedDate = new Date(this.date);
        const now = new Date();
        if (selectedDate > now) {
          this.EditForm.surgery_date_time = this.formatNewDate(this.date);
        } else {
          // Remove surgery_date_time from update payload if it's not in the future
          delete this.EditForm.surgery_date_time;
        }
      }

      // Prepare doctors data
      this.EditForm.doctors = this.doctorCards.map((card) => ({
        id: card.id,
        type: card.type,
      }));

      // Prepare assistants data
      this.EditForm.doctor_assist = this.assistantCards.map((card) => ({
        id: card.id,
        type: card.type,
      }));

      // Invoices are already prepared in handleServicesChange

      // Prepare consumables
      this.EditForm.consumables = this.EditForm.consumables.map((c) => ({
        item_id: c.item_id,
        quantity: c.quantity,
      }));

      // Add patient medical info
      this.EditForm.patient_case = this.patientCardData.patient_case;
      this.EditForm.examination = this.patientCardData.examination;
      this.EditForm.treatment = this.patientCardData.treatment;
      this.EditForm.diagnosis = this.patientCardData.diagnosis;
      this.EditForm.investigation = this.patientCardData.investigation;
      this.EditForm.money_collected =
        this.EditForm.money_collected === true ? 1 : 0;

      // Clean up empty fields
      for (let item in this.errorForm) {
        this.errorForm[item] = "";
        if (this.EditForm[item] == "" || this.EditForm[item] == null)
          delete this.EditForm[item];
      }

      this.params.form = this.EditForm;

      if (this.isEditMode && this.EditForm.id) {
        // Update existing surgery
        this.params.path = `/patient-surgery/${this.EditForm.id}`;
        this.$store
          .dispatch("update", this.params)
          .then(() => {
            this.loading = false;
            this.$router.push({ name: "surgery-patient-history" });
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "Success",
                variant: "success",
                text: "Surgery information updated successfully",
              },
            });
          })
          .catch((error) => {
            this.loading = false;
            if (error.response?.status === 400) {
              Object.entries(error.response.data).forEach(([key, value]) => {
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: "Validation Error",
                    variant: "danger",
                    text: value[0],
                  },
                });
              });
            }
          });
      } else {
        // Create new surgery
        this.params.path = "/patient-surgery";
        this.$store
          .dispatch("create", this.params)
          .then(() => {
            this.loading = false;
            this.$router.push({ name: "surgery-patient-history" });
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "Success",
                variant: "success",
                text: "Surgery information added successfully",
              },
            });
          })
          .catch((error) => {
            this.loading = false;
            if (error.response?.status === 400) {
              Object.entries(error.response.data).forEach(([key, value]) => {
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: "Validation Error",
                    variant: "danger",
                    text: value[0],
                  },
                });
              });
            }
          });
      }
    },
  },
};
</script>

<style scoped>
.surgery-form-container {
  padding: 1rem;
  max-width: 1400px;
  margin: 0 auto;
}

.go-back-container {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.main-card {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
  margin-bottom: 1rem;
  overflow: hidden;
}

.main-card >>> .card-body {
  padding: 1.5rem;
}

.card-content {
  padding: 0;
}

.section {
  padding: 1.25rem 0;
  position: relative;
}

.section:not(:last-child)::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(
    to right,
    transparent,
    #e5e7eb 20%,
    #e5e7eb 80%,
    transparent
  );
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.section-icon {
  color: #6366f1;
  flex-shrink: 0;
}

.section-title-text {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
  letter-spacing: 0.01em;
}

/* Section Color Themes */
.section-primary .section-icon {
  color: #6366f1;
}

.section-doctor .section-icon {
  color: #10b981;
}

.section-assistant .section-icon {
  color: #3b82f6;
}

.section-medical .section-icon {
  color: #f59e0b;
}

.section-services .section-icon {
  color: #8b5cf6;
}

.section-staff .section-icon {
  color: #ec4899;
}

.section-consumables .section-icon {
  color: #06b6d4;
}

.section-procedure .section-icon {
  color: #ef4444;
}

.compact-input {
  height: 36px;
  font-size: 0.875rem;
}

.compact-select >>> .vs__dropdown-toggle {
  min-height: 36px;
  font-size: 0.875rem;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
}

@media (max-width: 768px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
}

.doctor-card-wrapper {
  min-width: 0;
}

.doctor-card {
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  /* height: 100%; */
}

.doctor-card >>> .card-body {
  padding: 0.75rem;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
}

.doctor-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.doctor-icon {
  color: #6366f1;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
}

.card-header h6 {
  margin: 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f2937;
}

.card-header small {
  font-size: 0.7rem;
  color: #6b7280;
}

.remove-btn {
  width: 24px;
  height: 24px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;
}

.remove-btn:hover {
  background-color: #fee2e2;
  transform: scale(1.05);
}

.add-btn {
  min-width: 36px;
  height: 36px;
  padding: 0 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 6px;
  transition: all 0.2s;
}

.add-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.chips-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  min-height: 2rem;
}

.chip-badge {
  padding: 0.375rem 0.75rem;
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.chip-remove {
  cursor: pointer;
  margin-left: 0.4rem;
  display: inline-flex;
  align-items: center;
  transition: all 0.2s;
  padding: 0.1rem;
  border-radius: 4px;
}

.chip-remove:hover {
  background-color: rgba(0, 0, 0, 0.1);
  transform: scale(1.1);
}

.add-input-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.add-input-row .compact-input {
  flex: 1;
}

.add-consumable-row {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
}

.add-consumable-row .compact-input {
  height: 36px;
}

.submit-container {
  display: flex;
  justify-content: flex-end;
  padding: 1rem 0;
  margin-top: 1.5rem;
}

.submit-btn {
  min-width: 180px;
  height: 44px;
  font-size: 0.95rem;
  font-weight: 600;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
  transition: all 0.2s;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Modern card styling */
.doctor-card {
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  background: #ffffff;
}

.doctor-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
  border-color: #d1d5db;
}

/* Responsive adjustments */
@media (max-width: 576px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }

  .add-consumable-row {
    flex-direction: column;
  }

  .add-consumable-row > * {
    width: 100%;
  }
}

/* Ensure proper spacing */
.g-2 {
  --bs-gutter-x: 0.5rem;
  --bs-gutter-y: 0.5rem;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
